import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { subscribeToSaved, saveExperience, unsaveExperience } from '../services/savedExperiences';
import BucketListAuthModal from '../components/BucketListAuthModal';
import SaveToast from '../components/SaveToast';
import { isSavePromptSuppressed, markSavePromptDismissed } from '../utils/savePromptCooldown';

const SavedContext = createContext(null);

export function SavedProvider({ children }) {
  const { user } = useAuth();
  const [savedIds, setSavedIds] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveToast, setSaveToast] = useState(null);
  const pendingSaveIdRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setSavedIds([]);
      return undefined;
    }
    const unsub = subscribeToSaved(user.uid, setSavedIds);
    return unsub;
  }, [user]);

  useEffect(() => {
    const pendingId = pendingSaveIdRef.current;
    if (!user || !pendingId) return;

    const completePendingSave = async () => {
      try {
        if (!savedIds.includes(pendingId)) {
          await saveExperience(user.uid, pendingId);
        }
      } finally {
        pendingSaveIdRef.current = null;
      }
    };

    completePendingSave();
  }, [user, savedIds]);

  const toggleSave = useCallback(async (experienceId) => {
    if (!user) return;
    const saved = savedIds.includes(experienceId);
    if (saved) {
      await unsaveExperience(user.uid, experienceId);
    } else {
      await saveExperience(user.uid, experienceId);
    }
  }, [user, savedIds]);

  const isSaved = useCallback(
    (experienceId) => (user ? savedIds.includes(experienceId) : false),
    [user, savedIds]
  );

  const requestSave = useCallback((experienceId, anchorEl) => {
    if (user) {
      setSaveToast(null);
      toggleSave(experienceId);
      return;
    }

    if (isSavePromptSuppressed()) {
      pendingSaveIdRef.current = experienceId;
      setSaveToast({
        message: 'Make an account to save experiences',
        anchorEl: anchorEl || null
      });
      return;
    }

    setSaveToast(null);
    pendingSaveIdRef.current = experienceId;
    setSaveModalOpen(true);
  }, [user, toggleSave]);

  const dismissSaveToast = useCallback(() => {
    setSaveToast(null);
    pendingSaveIdRef.current = null;
  }, []);

  const handleMaybeLater = useCallback(() => {
    markSavePromptDismissed();
    pendingSaveIdRef.current = null;
    setSaveModalOpen(false);
    setSaveToast(null);
  }, []);

  const handleAuthSuccess = useCallback(() => {
    setSaveModalOpen(false);
  }, []);

  const openSaveModalFromToast = useCallback(() => {
    setSaveToast(null);
    setSaveModalOpen(true);
  }, []);

  const closeSaveAuthModal = useCallback(() => {
    setSaveModalOpen(false);
    setSaveToast(null);
    pendingSaveIdRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      savedIds,
      toggleSave,
      requestSave,
      isSaved,
      closeSaveAuthModal
    }),
    [savedIds, toggleSave, requestSave, isSaved, closeSaveAuthModal]
  );

  return (
    <SavedContext.Provider value={value}>
      {children}
      <BucketListAuthModal
        open={saveModalOpen}
        onMaybeLater={handleMaybeLater}
        onSuccess={handleAuthSuccess}
      />
      <SaveToast
        message={saveToast?.message}
        anchorEl={saveToast?.anchorEl}
        onAction={openSaveModalFromToast}
        onDismiss={dismissSaveToast}
      />
    </SavedContext.Provider>
  );
}

export function useSaved() {
  const ctx = useContext(SavedContext);
  if (!ctx) throw new Error('useSaved must be used within SavedProvider');
  return ctx;
}
